/**
 * OperatingManualPage — first real Blueprint surface.
 *
 * Composes the structural chrome (SeriesBanner, AuditFooter) around a markdown
 * body produced by Layer 4. The body is rendered through the small Markdown
 * component which:
 *   - Maps "## Section N — Title" → SectionHeader
 *   - Maps "> [!Tip|Watch|Remember|Key]" → Callout variants
 *   - Wraps hedge_phrases in HedgeMark per locked Q4 design pattern
 *
 * Acceptance shape (from /api/operating-manual response):
 *   {
 *     title, subject_first_name, viewer_mode, subject_mode,
 *     operating_manual_markdown,
 *     hedge_phrases, lite_placeholders_emitted, section_12_rendered,
 *     tension_alignment_guardrail_rendered, goal_alignment_worked_example_included,
 *     engine_version, blueprint_id, generation_latency_ms
 *   }
 */

import SeriesBanner from "../components/SeriesBanner.jsx";
import AuditFooter from "../components/AuditFooter.jsx";
import MarkdownV2 from "../components/MarkdownV2.jsx";
import PrintToPdfButton from "../components/PrintToPdfButton.jsx";
import BlueprintChat from "../components/BlueprintChat.jsx";

const OPERATING_MANUAL_PRESETS = [
  { label: "How should I open our next conversation?",       preset_text: "How should I open my next conversation with this person?" },
  { label: "What if they go quiet on me?",                   preset_text: "If they go quiet on me — slow replies, cooled interest — what's the move that lands for this profile?" },
  { label: "What builds trust fastest?",                     preset_text: "What builds trust fastest with this person, and what would feel hollow or salesy to them?" },
  { label: "Deliver hard news",                              preset_text: "Help me frame hard news for this person — a price increase, a delay, a no — what landing pattern fits?" },
  { label: "How do they handle pressure?",                   preset_text: "How does this person tend to handle pressure when stakes get high?" },
  { label: "What are they reaching for next?",               preset_text: "What is this person probably reaching for next — in their role, their business, their standing — and how do I help them get it?" },
];

export default function OperatingManualPage({
  data,
  consentRecord,
  pageIndicator = "page 1 of 1",
  bearerToken,
  tier,
}) {
  if (!data) return null;

  const {
    title,
    subject_first_name,
    operating_manual_markdown,
    hedge_phrases = [],
    engine_version,
    blueprint_id,
    tension_alignment_guardrail_rendered,
    goal_alignment_worked_example_included,
    subject_mode,
  } = data;

  const generatedDate = data.generated_at_iso
    ? new Date(data.generated_at_iso).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);

  return (
    <article
      className="ppr-operating-manual-page"
      style={{
        background: "var(--ppr-color-background-page)",
        maxWidth: 780,
        margin: "0 auto",
        padding: "var(--ppr-space-6)",
        fontFamily: "var(--ppr-typography-font-family-body)",
        color: "var(--ppr-color-text-primary)",
      }}
      data-component="OperatingManualPage"
      data-blueprint-type="operating_manual"
      data-subject-mode={subject_mode}
    >
      <SeriesBanner type="Playbook" dateLabel={generatedDate} />

      <header
        style={{
          marginTop: 28,
          marginBottom: 4,
        }}
      >
        <h1
          style={{
            fontFamily: "var(--ppr-typography-font-family-display)",
            fontSize: "var(--ppr-typography-font-size-3xl)",
            fontWeight: "var(--ppr-typography-font-weight-regular)",
            color: "var(--ppr-color-text-primary)",
            letterSpacing: "var(--ppr-typography-letter-spacing-snug)",
            margin: 0,
            lineHeight: "var(--ppr-typography-line-height-tight)",
          }}
        >
          {title || `Playbook: ${subject_first_name || ""}`}
        </h1>
        {subject_mode && subject_mode !== "Full" ? (
          <ModeBadge mode={subject_mode} />
        ) : null}
      </header>

      {/* MarkdownV2 with subject-name auto-bold (#80). The profile's
          first name is exposed on the data envelope. */}
      <MarkdownV2
        source={operating_manual_markdown || ""}
        subjectNames={subject_first_name ? [subject_first_name] : []}
      />

      {/*
        SelfAttestationStrip removed from user-facing render per V1 staging
        follow-up #1 (brand discipline). The underlying flags
        (tension_alignment_guardrail_rendered, goal_alignment_worked_example_included)
        still flow on the blueprints table for fairness-audit consumption —
        they're server-side attestations, not user-facing scaffolding.
      */}

      {bearerToken && blueprint_id && (
        <BlueprintChat
          blueprintId={blueprint_id}
          blueprintType="operating_manual"
          presetPrompts={OPERATING_MANUAL_PRESETS}
          bearerToken={bearerToken}
          tier={tier}
          mode="pre_meeting"
        />
      )}

      <AuditFooter
        engineVersion={engine_version}
        consentRecord={consentRecord}
        blueprintId={blueprint_id}
        pageIndicator={pageIndicator}
      />

      {/* Print-to-PDF (#73). Hidden on print via the styles.css rule. */}
      <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
        <PrintToPdfButton label="Save as PDF" />
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Internal pieces — small, only used by this page
// ---------------------------------------------------------------------------

function ModeBadge({ mode }) {
  const styles = {
    Lite: {
      bg: "var(--ppr-color-background-muted)",
      fg: "var(--ppr-color-text-muted)",
      label: "LITE PROFILE",
    },
    Departed: {
      bg: "var(--ppr-color-background-muted)",
      fg: "var(--ppr-color-text-secondary)",
      label: "DEPARTED",
    },
    LegalHold: {
      bg: "var(--ppr-color-accent-coral-very-soft)",
      fg: "var(--ppr-color-accent-coral-dark)",
      label: "LEGAL HOLD",
    },
  }[mode] || null;
  if (!styles) return null;
  return (
    <span
      style={{
        display: "inline-block",
        marginTop: 10,
        padding: "4px 10px",
        background: styles.bg,
        color: styles.fg,
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "11px",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
      }}
    >
      {styles.label}
    </span>
  );
}

/*
 * Note: SelfAttestationStrip used to live here, surfacing structural-commitment
 * checks (Tension Alignment guardrail rendered, Goal Alignment worked example
 * included) to the reader. Removed per V1 staging follow-up #1 — that copy
 * is CI scaffolding, not user-facing footer content. The blueprints table
 * still carries the underlying flags for the fairness audit surface.
 */
